import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Music, Heart, ExternalLink, LogOut, Check, Trash2,
  Play, X, Headphones, RefreshCw,
} from "lucide-react";
import { ytId, embedFor } from "../lib/media";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORE_KEY = "hh_dj_password";

export default function DJDashboard() {
  const [password, setPassword] = useState(() => {
    try {
      return localStorage.getItem(STORE_KEY) || "";
    } catch {
      return "";
    }
  });
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);

  const [wishes, setWishes] = useState([]);
  const [sections, setSections] = useState([]);
  const [tab, setTab] = useState("wishes"); // 'wishes' | 'plan'
  const [playing, setPlaying] = useState(null);
  const [loading, setLoading] = useState(false);

  // Auto-verify stored password
  useEffect(() => {
    (async () => {
      if (!password) {
        setChecking(false);
        return;
      }
      try {
        await axios.post(`${API}/dj/login`, { password });
        setAuthed(true);
      } catch {
        try {
          localStorage.removeItem(STORE_KEY);
        } catch {
          /* ignore */
        }
      }
      setChecking(false);
    })();
  }, [password]);

  const load = useCallback(async () => {
    if (!authed) return;
    setLoading(true);
    try {
      const [w, m] = await Promise.all([
        axios.get(`${API}/dj/wishes`, { params: { password } }),
        axios.get(`${API}/dj/music`, { params: { password } }),
      ]);
      setWishes(w.data || []);
      setSections(m.data?.sections || []);
    } catch {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [authed, password]);

  useEffect(() => {
    load();
    // Poll wishes every 15s so DJ sees new guest wishes near-live
    const iv = setInterval(() => {
      if (authed) load();
    }, 15000);
    return () => clearInterval(iv);
  }, [authed, load]);

  if (checking) return <div className="min-h-screen bg-sage-950" />;

  if (!authed) return <DJLoginScreen onLogin={(pw) => {
    try { localStorage.setItem(STORE_KEY, pw); } catch { /* ignore */ }
    setPassword(pw);
  }} />;

  const logout = () => {
    try { localStorage.removeItem(STORE_KEY); } catch { /* ignore */ }
    setPassword("");
    setAuthed(false);
  };

  const togglePlayed = async (id, played) => {
    // Optimistic update
    setWishes((prev) =>
      prev.map((w) => (w.id === id ? { ...w, played } : w))
    );
    try {
      await axios.patch(
        `${API}/dj/wishes/${id}`,
        { played },
        { params: { password } }
      );
    } catch {
      toast.error("Konnte nicht aktualisieren");
      load();
    }
  };

  const removeWish = async (id) => {
    if (!window.confirm("Diesen Wunsch wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/wishes/${id}`, { params: { password } });
      setWishes((p) => p.filter((w) => w.id !== id));
      toast.success("Gelöscht");
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const openWishes = wishes.filter((w) => !w.played);
  const playedWishes = wishes.filter((w) => w.played);
  const totalTracks = sections.reduce((s, sec) => s + (sec.tracks?.length || 0), 0);

  return (
    <div data-testid="dj-dashboard" className="min-h-screen bg-sage-950 text-sage-100">
      {/* Header */}
      <header className="border-b border-sage-800 bg-sage-900/80 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-4 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="font-serif text-lg md:text-xl inline-flex items-center gap-2">
              <Headphones size={18} className="text-sage-300" />
              DJ · Nico &amp; Nicole
            </div>
            <p className="text-[11px] text-sage-400 tracking-wide">
              01.08.2026 · Gut Haferkorn
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1 text-sage-300 hover:text-white text-xs px-3 py-2 rounded-full border border-sage-700 hover:bg-sage-800"
              aria-label="Aktualisieren"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              <span className="hidden sm:inline">Aktualisieren</span>
            </button>
            <button
              data-testid="dj-logout"
              onClick={logout}
              className="inline-flex items-center gap-1 rounded-full bg-sage-700 hover:bg-sage-600 text-white px-3 md:px-4 py-2 text-xs uppercase tracking-wide"
            >
              <LogOut size={14} />
              <span className="hidden sm:inline">Ausloggen</span>
            </button>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="container mx-auto px-4 md:px-8 pb-3">
          <div className="inline-flex items-center bg-sage-800 border border-sage-700 rounded-full p-1">
            <button
              type="button"
              data-testid="dj-tab-wishes"
              onClick={() => setTab("wishes")}
              className={`px-4 py-2 text-xs uppercase tracking-wider rounded-full transition-colors inline-flex items-center gap-1.5 ${
                tab === "wishes" ? "bg-sage-500 text-white" : "text-sage-300 hover:text-white"
              }`}
            >
              <Heart size={12} /> Wünsche
              <span className="ml-1 rounded-full bg-black/30 px-1.5 py-0.5 text-[10px]">
                {openWishes.length}
              </span>
            </button>
            <button
              type="button"
              data-testid="dj-tab-plan"
              onClick={() => setTab("plan")}
              className={`px-4 py-2 text-xs uppercase tracking-wider rounded-full transition-colors inline-flex items-center gap-1.5 ${
                tab === "plan" ? "bg-sage-500 text-white" : "text-sage-300 hover:text-white"
              }`}
            >
              <Music size={12} /> Ablaufplan
              <span className="ml-1 rounded-full bg-black/30 px-1.5 py-0.5 text-[10px]">
                {totalTracks}
              </span>
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-8 py-8">
        {tab === "wishes" ? (
          <WishesPanel
            openWishes={openWishes}
            playedWishes={playedWishes}
            onTogglePlayed={togglePlayed}
            onRemove={removeWish}
          />
        ) : (
          <PlanPanel
            sections={sections}
            playing={playing}
            setPlaying={setPlaying}
          />
        )}
      </main>
    </div>
  );
}

// ------- Login screen -------
const DJLoginScreen = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await axios.post(`${API}/dj/login`, { password: pw });
      onLogin(pw);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-sage-950 flex items-center justify-center p-6">
      <form
        onSubmit={submit}
        data-testid="dj-login-form"
        className="w-full max-w-sm bg-sage-900 border border-sage-800 rounded-2xl p-8 shadow-xl"
      >
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-sage-700 mx-auto flex items-center justify-center mb-3">
            <Headphones size={22} className="text-white" />
          </div>
          <h1 className="font-serif text-2xl text-white">DJ-Zugang</h1>
          <p className="text-xs text-sage-400 mt-1">
            Nico &amp; Nicole · 01.08.2026
          </p>
        </div>
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-sage-400">Passwort</span>
          <input
            data-testid="dj-password"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
            className="mt-2 w-full rounded-lg border border-sage-700 bg-sage-800 text-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-sage-500"
          />
        </label>
        {err && <p className="text-red-400 text-xs mt-3">{err}</p>}
        <button
          type="submit"
          disabled={busy || !pw}
          data-testid="dj-login-submit"
          className="mt-6 w-full rounded-full bg-sage-500 hover:bg-sage-400 disabled:bg-sage-700 text-white px-6 py-3 text-sm uppercase tracking-wider"
        >
          {busy ? "Prüft..." : "Einloggen"}
        </button>
      </form>
    </div>
  );
};

// ------- Wishes panel -------
const WishesPanel = ({ openWishes, playedWishes, onTogglePlayed, onRemove }) => (
  <div className="space-y-8">
    <section>
      <h2 className="font-serif text-2xl md:text-3xl mb-3">
        Offene Wünsche{" "}
        <span className="text-sage-400 text-lg">({openWishes.length})</span>
      </h2>
      {openWishes.length === 0 ? (
        <div className="bg-sage-900 border border-sage-800 rounded-2xl p-10 text-center text-sage-400">
          Noch keine offenen Wünsche. Sobald Gäste etwas wünschen, erscheinen die
          Songs hier — sortiert nach Beliebtheit.
        </div>
      ) : (
        <ul className="space-y-2">
          {openWishes.map((w, i) => (
            <WishRow
              key={w.id}
              rank={i + 1}
              wish={w}
              onTogglePlayed={onTogglePlayed}
              onRemove={onRemove}
            />
          ))}
        </ul>
      )}
    </section>

    {playedWishes.length > 0 && (
      <section>
        <h2 className="font-serif text-lg text-sage-400 mb-3">
          Bereits gespielt ({playedWishes.length})
        </h2>
        <ul className="space-y-2 opacity-70">
          {playedWishes.map((w) => (
            <WishRow
              key={w.id}
              wish={w}
              onTogglePlayed={onTogglePlayed}
              onRemove={onRemove}
              dim
            />
          ))}
        </ul>
      </section>
    )}
  </div>
);

const WishRow = ({ rank, wish, onTogglePlayed, onRemove, dim }) => {
  const vid = ytId(wish.youtube_url) || (wish.youtube_url && wish.youtube_url.includes("open.spotify.com") ? "sp" : null);
  return (
    <li
      data-testid={`dj-wish-${wish.id}`}
      className={`bg-sage-900 border border-sage-800 rounded-xl px-4 py-3 flex items-center gap-3 ${
        dim ? "text-sage-500" : ""
      }`}
    >
      {typeof rank === "number" && (
        <div
          className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-sm font-serif ${
            rank <= 3 ? "bg-sage-500 text-white" : "bg-sage-800 text-sage-300"
          }`}
        >
          {rank}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-medium ${wish.played ? "line-through" : ""}`}>
            {wish.title}
          </span>
          {wish.artist && (
            <span className="text-sm text-sage-400 truncate">· {wish.artist}</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-sage-400 mt-0.5">
          {wish.guest_name && <span>von {wish.guest_name}</span>}
          {vid && (
            <a
              href={wish.youtube_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 hover:text-white"
            >
              <ExternalLink size={10} /> {wish.youtube_url.includes("spotify.com") ? "Spotify" : "YouTube"}
            </a>
          )}
          <span className="inline-flex items-center gap-1 text-sage-300">
            <Heart size={11} fill="currentColor" /> {wish.votes}
          </span>
        </div>
      </div>
      <button
        type="button"
        data-testid={`dj-toggle-${wish.id}`}
        onClick={() => onTogglePlayed(wish.id, !wish.played)}
        className={`shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium ${
          wish.played
            ? "bg-sage-800 text-sage-300 hover:bg-sage-700"
            : "bg-sage-500 hover:bg-sage-400 text-white"
        }`}
        title={wish.played ? "Als offen markieren" : "Als gespielt markieren"}
      >
        {wish.played ? <X size={12} /> : <Check size={12} />}
        <span className="hidden sm:inline">
          {wish.played ? "Öffnen" : "Gespielt"}
        </span>
      </button>
      <button
        type="button"
        onClick={() => onRemove(wish.id)}
        className="shrink-0 text-sage-500 hover:text-red-400"
        aria-label="Wunsch entfernen"
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
};

// ------- Plan panel (read-only music from admin) -------
const PlanPanel = ({ sections, playing, setPlaying }) => {
  if (!sections.length) {
    return (
      <div className="bg-sage-900 border border-sage-800 rounded-2xl p-10 text-center text-sage-400">
        Kein Musik-Ablaufplan hinterlegt.
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {sections.map((sec, i) => (
        <section
          key={sec.id}
          data-testid={`dj-section-${sec.id}`}
          className="bg-sage-900 border border-sage-800 rounded-2xl overflow-hidden"
        >
          <header className="px-5 py-3 bg-sage-800/70 border-b border-sage-800">
            <div className="flex items-baseline gap-3">
              <span className="font-serif text-sage-400 text-sm">{i + 1}.</span>
              <h3 className="font-serif text-xl text-white">{sec.name}</h3>
            </div>
            {sec.notes && (
              <p className="text-xs text-sage-400 mt-1">{sec.notes}</p>
            )}
          </header>
          {sec.tracks.length === 0 ? (
            <p className="px-5 py-6 text-sm text-sage-500 text-center">
              Keine Songs.
            </p>
          ) : (
            <ul className="divide-y divide-sage-800">
              {sec.tracks.map((t) => {
                const embed = embedFor(t.youtube_url, { autoplay: true });
                const isSpotify = embed?.provider === "spotify";
                const isPlaying = playing === t.id;
                return (
                  <li key={t.id} className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-medium truncate">
                            {t.title || <span className="text-sage-500">(ohne Titel)</span>}
                          </span>
                          {t.artist && (
                            <span className="text-sm text-sage-400">· {t.artist}</span>
                          )}
                        </div>
                        {t.note && (
                          <p className="text-[11px] text-sage-400 mt-0.5">{t.note}</p>
                        )}
                      </div>
                      {embed && (
                        <>
                          <button
                            type="button"
                            data-testid={`dj-play-${t.id}`}
                            onClick={() => setPlaying(isPlaying ? null : t.id)}
                            className="rounded-full p-2 bg-sage-500 hover:bg-sage-400 text-white"
                            aria-label={isPlaying ? "Schließen" : "Anspielen"}
                          >
                            {isPlaying ? <X size={14} /> : <Play size={14} />}
                          </button>
                          <a
                            href={t.youtube_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded-full p-2 text-sage-400 hover:text-white"
                            aria-label={isSpotify ? "In Spotify öffnen" : "In YouTube öffnen"}
                          >
                            <ExternalLink size={14} />
                          </a>
                        </>
                      )}
                    </div>
                    {isPlaying && embed && (
                      <div className={`mt-3 rounded-xl overflow-hidden border border-sage-800 max-w-xl ${
                        isSpotify ? "bg-black/40" : "bg-black aspect-video"
                      }`}>
                        <iframe
                          title={`${embed.provider} ${t.title || ""}`}
                          src={embed.src}
                          className="w-full"
                          style={embed.height ? { height: embed.height } : undefined}
                          height={embed.height || undefined}
                          frameBorder="0"
                          allow="autoplay; encrypted-media; clipboard-write; fullscreen"
                          allowFullScreen
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
};
